class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (event.target.step && parseInt(event.target.step) && inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const sectionsToRender = [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ];

    // Add main-cart-footer if it exists and has a dataset.id
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter && cartFooter.dataset.id) {
      sectionsToRender.push({
        id: 'main-cart-footer',
        section: cartFooter.dataset.id,
        selector: '.js-contents',
      });
    }

    return sectionsToRender;
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            if (parsedState.sections && parsedState.sections[section.section]) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          });

          // Update cart totals if they exist but weren't updated via sections
          this.updateCartTotals(parsedState);
          this.updateFreeShippingIndicator(parsedState);
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  updateCartTotals(cartData) {
    // Update line item totals for each cart item
    if (cartData.items) {
      cartData.items.forEach((item, index) => {
        const lineNumber = index + 1;
        const lineItemTotals = document.querySelectorAll(`#CartItem-${lineNumber} .cart-item__price-wrapper`);

        if (lineItemTotals.length > 0) {
          lineItemTotals.forEach((totalElement) => {
            // Create the price HTML
            let priceHTML = '';
            if (item.original_line_price !== item.final_line_price) {
              priceHTML = `
                <dl class="cart-item__discounted-prices">
                  <dt class="visually-hidden">Regular price</dt>
                  <dd>
                    <s class="cart-item__old-price price price--end">
                      ${this.formatMoney(item.original_line_price)}
                    </s>
                  </dd>
                  <dt class="visually-hidden">Sale price</dt>
                  <dd class="price price--end">
                    ${this.formatMoney(item.final_line_price)}
                  </dd>
                </dl>
              `;
            } else {
              priceHTML = `
                <span class="price price--end">
                  ${this.formatMoney(item.original_line_price)}
                </span>
              `;
            }
            totalElement.innerHTML = priceHTML;
          });
        }
      });
    }

    // Update cart summary subtotal and total
    const subtotalElements = document.querySelectorAll('.cart-totals .flex.justify-between span:last-child');
    if (subtotalElements.length > 0 && cartData.items_subtotal_price !== undefined) {
      subtotalElements[0].textContent = this.formatMoney(cartData.items_subtotal_price);
    }

    const totalElements = document.querySelectorAll('.totals__total-value');
    if (totalElements.length > 0 && cartData.total_price !== undefined) {
      totalElements.forEach(el => {
        el.textContent = this.formatMoney(cartData.total_price);
      });
    }
  }

  updateFreeShippingIndicator(cartData) {
    // Get threshold from the main cart footer element
    const cartFooter = document.getElementById('main-cart-footer');
    if (!cartFooter || !cartFooter.dataset.freeShippingThreshold) return;

    const threshold = parseInt(cartFooter.dataset.freeShippingThreshold);
    const remaining = threshold - cartData.total_price;
    const qualifiedText = cartFooter.dataset.freeShippingQualifiedText || 'You\'ve qualified for free shipping!';

    const freeShippingIndicator = document.querySelector('.free-shipping-indicator');

    // If threshold is reached, show the indicator
    if (remaining <= 0) {
      if (freeShippingIndicator) {
        // If it exists but is hidden, show it
        freeShippingIndicator.style.display = 'block';
      } else {
        // If it doesn't exist, create it
        const jsContents = document.querySelector('#main-cart-footer .js-contents');
        if (jsContents) {
          const indicator = document.createElement('div');
          indicator.className = 'free-shipping-indicator max-md:text-sm my-6';
          // Use innerHTML to support HTML tags like <strong>
          const textDiv = document.createElement('div');
          textDiv.innerHTML = qualifiedText;
          indicator.innerHTML = `
            <div class="progress-bar mt-2">
              <div class="progress-bar-inner" style="width: 100%; display: block;"></div>
            </div>
          `;
          indicator.insertBefore(textDiv, indicator.firstChild);
          jsContents.insertBefore(indicator, jsContents.firstChild);
        }
      }
    } else {
      // Hide the indicator if threshold is not reached
      if (freeShippingIndicator) {
        freeShippingIndicator.style.display = 'none';
      }
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
    }
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Process free shipping text to allow HTML rendering
document.addEventListener('DOMContentLoaded', () => {
  const freeShippingText = document.querySelector('.free-shipping-text');
  if (freeShippingText) {
    const textContent = freeShippingText.textContent;
    freeShippingText.innerHTML = textContent;
  }

  // Terms and Conditions Validation
  function initTermsValidation() {
    const cartDrawerCheckoutBtn = document.getElementById('CartDrawer-Checkout');
    const cartDrawerTermsCheckbox = document.getElementById('CartDrawer-Terms');

    const cartNotificationCheckoutBtn = document.getElementById('CartNotification-Checkout');
    const cartNotificationTermsCheckbox = document.getElementById('CartNotification-Terms');

    // Get all checkout buttons (including dynamically loaded ones)
    const cartCheckoutButtons = document.querySelectorAll('#checkout, #checkout-summary, button[name="checkout"]');
    const cartTermsCheckbox = document.getElementById('Cart-Terms');

    function validateTermsCheckbox(checkoutButton, termsCheckbox) {
      if (!checkoutButton || !termsCheckbox) return;

      checkoutButton.addEventListener('click', (event) => {
        if (!termsCheckbox.checked) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          // Show error message
          const termsLabel = termsCheckbox.nextElementSibling;
          if (termsLabel) {
            termsLabel.style.color = 'red';
            setTimeout(() => {
              termsLabel.style.color = '';
            }, 3000);
          }

          // Focus on the checkbox
          termsCheckbox.focus();

          // Alert the user
          alert('Please agree to the terms and conditions before proceeding to checkout.');

          return false;
        }
      }, true); // Use capture phase

      // Remove error styling when checkbox is checked
      termsCheckbox.addEventListener('change', () => {
        const termsLabel = termsCheckbox.nextElementSibling;
        if (termsLabel) {
          termsLabel.style.color = '';
        }
      });
    }

    // Apply validation to cart drawer checkout button
    validateTermsCheckbox(cartDrawerCheckoutBtn, cartDrawerTermsCheckbox);

    // Apply validation to cart notification checkout button
    validateTermsCheckbox(cartNotificationCheckoutBtn, cartNotificationTermsCheckbox);

    // Apply validation to all cart page checkout buttons
    cartCheckoutButtons.forEach(button => {
      validateTermsCheckbox(button, cartTermsCheckbox);
    });
  }

  // Initialize validation
  initTermsValidation();

  // Re-initialize after dynamic content updates
  document.addEventListener('cart:updated', initTermsValidation);
});
